import CoreBase from '../../../../../../services/engines/CoreBase';
import responseHelper from '../../../../../../services/ResponseHelper';
import userTopicService from '../../../../../../services/UserTopicService';
import ComponentNotifier from '../../../../../../services/wss/ComponentNotifier';
import searchDrawerFetcher from './SearchDrawerFetcher';
import { addOrUpdate } from '../../../../../../utils/arrayUtils';

const PAGE_SIZE = 25;

/**
 * SearchResultProvider — per-panel search state.
 * Not a singleton — one instance per FloatingContentPanel, created by FloatingPanelEngine.
 * panelId used for logging and as ComponentNotifier channel — only the owning panel gets notified.
 *
 * Naming convention: noun.verb — provider.provide, fetcher.fetch
 *
 * @param {string} panelId - e.g. 'fcp-1'
 */
export default class SearchResultProvider extends CoreBase {
    _panelId = '';

    // _name = SearchResultProvider_{panelId} — each provider pushes events on its own channel.
    // useSearchResults(panelId) subscribes to that exact channel so only the owning panel repaints.
    _name = '';

    _query = '';
    _topic = '';
    _page = 1;
    _total = 0;
    _results = [];
    _selectedEntry = null;
    _loading = false;

    constructor(panelId) {
        super();
        this._panelId = panelId;
        this._name = `SearchResultProvider_${panelId}`; // unique channel per panel — no cross-panel notify
    }

    // ─── public ──────────────────────────────────────────

    isTheSame = (query, topic) => {
        try {
            const q = query?.trim() ?? '';
            const t = topic?.trim() ?? '';

            if (this._topic !== t) return false;
            if (this._query !== q) return false;

            return true;
        } catch (error) {
            this.logError('isTheSame', `${error.message}`);
            return true;
        }
    };

    /**
     * Fetches results for current query + topic + page, notifies panel when done.
     * Passes panelId as rk for server-side correlation logging.
     * @param {string} query
     */
    provide = async (query, topic = '') => {
        try {
            if (topic !== '') {
                this._topic = topic.trim();
            }

            this._query = query.trim();
            this._page = 1;
            this._loading = true;
            this._notify();

            const data = await searchDrawerFetcher.fetch(this._query, this._topic, this._page, PAGE_SIZE, this._panelId);
            if (!data) {
                this._loading = false;
                this._notify();
                return;
            }

            this._results = data.items;
            this._total = data.total;
            this._loading = false;
            this._notify();
        } catch (error) {
            this.logError('provide', `${error.message}`);
            return;
        }
    };

    /**
     * Sets topic filter and re-provides from page 1.
     * @param {string} topic
     */
    setTopic = (topic) => {
        const q = this._query ?? '';
        const t = topic.trim();

        this.provide(q, t);
    };

    nextPage = async () => {
        const totalPages = Math.ceil(this._total / PAGE_SIZE);
        if (this._page >= totalPages) return;
        const page = this._page + 1;
        await this.fetchPage(page);
    };

    prevPage = async () => {
        if (this._page <= 1) return;
        const page = this._page - 1;
        await this.fetchPage(page);
    };

    /**
     * Fetches results for current query + topic + page, notifies panel when done.
     * @param {number} page
     */
    fetchPage = async (page) => {
        try {
            this._loading = true;
            this._notify();

            this._page = page;
            const data = await searchDrawerFetcher.fetch(this._query, this._topic, this._page, PAGE_SIZE, this._panelId);
            if (!data) {
                this._loading = false;
                this._notify();
                return;
            }

            this._results = data.items;
            this._total = data.total;
            this._loading = false;
            this._notify();
        } catch (error) {
            this.logError('provide', `${error.message}`);
            return;
        }
    };

    /**
     * Adds or updates an entry in the results array.
     * Only active when query is '__recent__'.
     * Also updates selectedEntry if rk matches.
     * @param {object} entry
     */
    addToResult = (entry) => {
        try {
            if (this._query !== '__recent__') return;

            if (this._selectedEntry?.rk === entry.rk) {
                this._selectedEntry = entry;
            }

            this._results = addOrUpdate(this._results, entry);
            this._notify();
        } catch (error) {
            this.logDebug('addToResult', error.message);
        }
    };

    selectEntry = (entry) => {
        this._selectedEntry = entry;
        this._notify();
    };

    cleanup = async (id) => {
        try {
            const dto = { data: { rk: id, action: 'cleanup' } };

            const resp = await userTopicService.searchCleanup(dto);
            if (responseHelper.check(resp, '', `${this._name}`) < 0) {
                this.logError('cleanup', `status: ${resp?.statusCode}`);
                return;
            }
        } catch (error) {
            this.logError('cleanup', `${error.message}`);
        }
    };

    // ─── getters ─────────────────────────────────────────

    getResults = () => this._results;
    getTotal = () => this._total;
    getPage = () => this._page;
    getPageSize = () => PAGE_SIZE;
    getQuery = () => this._query;
    getTopic = () => this._topic;
    getSelectedEntry = () => this._selectedEntry;
    isLoading = () => this._loading;

    /**
     * Returns all paint-relevant state in one call — consumed by useSearchResults.
     * @returns {{ results, total, page, query, topic, selectedEntry, loading }}
     */
    getProps = () => ({
        results: this._results,
        total: this._total,
        page: this._page,
        query: this._query,
        topic: this._topic,
        selectedEntry: this._selectedEntry,
        loading: this._loading,
    });

    // ─── private ─────────────────────────────────────────

    _notify = () => {
        ComponentNotifier.notify(this._name, 'dataChanged', {});
    };
}
